/**
 * Dark-mode palette — single source of truth for `_dark` semantic token values.
 *
 * Neutrals share the navy hue family (~210–215°) as desaturated blue-slate steps.
 * Avoid achromatic greys here — they read as “default UI grey” on blue surfaces.
 */
export const darkPalette = {
  bgApp: '#08101A',
  bgMain: '#0D1623',
  bgTopbar: '#060E19',
  bgSidebar: '#101925',
  bgCard: '#101925',
  bgCardRaised: '#16202E',
  bgTableHeader: '#172232',

  borderSubtle: '#20334F',
  borderControl: '#33465A',
  /** Blue-slate divider steps — same hue family as borders, not neutral grey. */
  dividerSubtle: '#1C2A3D',
  dividerStrong: '#2A3D55',

  textPrimary: '#D5DEE9',
  /** Desaturated blue-slate text ramp — not achromatic grey. */
  textSecondary: '#94A8BE',
  textMuted: '#6B8299',
  textDisabled: '#4E6278',

  /** Primary interactive blue — logo mark, links on hover, charts, focus rings. */
  bluePrimary: '#3284FE',
  /** Emphasis on tinted surfaces — active nav, tags, links at rest (pairs with blueActiveBg). */
  blueSoft: '#5296F0',
  /** Navy pill behind active nav rows and soft icon tiles. */
  blueActiveBg: '#122747',
  /** Alias of bluePrimary — keeps accent.strong call sites on the same hue in dark mode. */
  blueAction: '#3284FE',

  greenPrimary: '#4BBD63',
  greenSoft: '#58C370',

  /** Patient queue screen — design tokens (dark). */
  queuePanelBgTop: '#101C2B',
  queuePanelBgBottom: '#0D1826',
  queuePanelBorder: '#203A58',
  queueSearchBg: '#0A1623',
  queueSearchBorder: '#2A405B',
  queueExpandedBg: 'rgba(13, 27, 43, 0.72)',
  queueAvatarBg: '#173967',
  queueConsultBtnBg: '#BFD9FF',
  queueConsultBtnText: '#071321',
  queueConsultBtnBorder: '#CFE4FF',
  queueAdmitTop: '#367BFF',
  queueAdmitBottom: '#2563EB',
  queueAdmitBorder: '#4A8DFF',
  queueMetricBlueIcon: '#3478F6',
  queueMetricBlueBg: 'rgba(52, 120, 246, 0.14)',
  queueMetricYellowIcon: '#FACC15',
  queueMetricYellowBg: 'rgba(245, 158, 11, 0.14)',
  queueMetricGreenIcon: '#6EE7A0',
  queueMetricGreenBg: 'rgba(34, 197, 94, 0.14)',
  queueMetricPurpleIcon: '#C084FC',
  queueMetricPurpleBg: 'rgba(168, 85, 252, 0.14)',
  queueWaitingBadgeBg: 'rgba(245, 158, 11, 0.12)',
  queueWaitingBadgeText: '#FACC15',
  queueWaitingBadgeBorder: 'rgba(250, 204, 21, 0.4)',
  queueScheduledBadgeBg: 'rgba(37, 99, 235, 0.18)',
  queueScheduledBadgeText: '#73A7FF',
  queueScheduledBadgeBorder: 'rgba(37, 99, 235, 0.45)',
  queueNextBadgeBg: 'rgba(34, 197, 94, 0.16)',
  queueNextBadgeText: '#7EE6A1',
  queueNextBadgeBorder: 'rgba(34, 197, 94, 0.35)',
} as const
